import React, { useMemo, useRef } from 'react';
import { trackButtonActivity } from 'src/functions/propagateQueryParams';
import getButtonStyleByColorScheme, { ColorScheme } from 'src/styles/functions/getButtonStyleByColorScheme';
import { ButtonContainerProps, ButtonPrimaryContainer } from './ButtonPrimary.styles';
import { StandardParagraphSize } from 'src/elements/typography/paragraph.styles';

export interface ButtonProps extends ButtonContainerProps {
  onClick?: (e?: any) => void;
  text: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  id?: string;
  colorScheme?: ColorScheme;
  colorSchemeVariant?: 'primary' | 'secondary' | 'tertiary';
  disabled?: boolean;
  type?: 'button' | 'submit';
  standardSize?: StandardParagraphSize;
}

const ButtonPrimary = (props: ButtonProps): JSX.Element => {
  const { icon, iconPosition, standardSize, sizeSubLg, sizeXs } = props;
  const buttonRef = useRef<HTMLButtonElement>();
  const buttonColors = useMemo(
    () => getButtonStyleByColorScheme(props.colorScheme ?? 'StandardYellow', props.colorSchemeVariant ?? 'primary'),
    []
  );

  // Extend onClick listener for Trackers, etc.
  const trackOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.onClick) {
      props.onClick(e);
    }
    // >> Additional trackers
    trackButtonActivity(buttonRef.current, props.text);
  };

  const size = props.size ?? StandardParagraphSize[standardSize]?.desktop;
  const sizeLg = props.sizeLg ?? StandardParagraphSize[standardSize]?.lg;
  const sizeSm = props.sizeSm ?? StandardParagraphSize[standardSize]?.sm;

  const sizeProps = {
    size,
    sizeLg,
    sizeSm,
    sizeSubLg,
    sizeXs,
  };

  return (
    <ButtonPrimaryContainer
      className='button-primary'
      ref={buttonRef}
      onClick={trackOnClick}
      color={props.textColor ?? buttonColors.backgroundColor}
      textColor={buttonColors.textColor}
      id={props.id}
      type={props.type ?? 'button'}
      disabled={props.disabled}
      customBorderRadius={props.customBorderRadius}
      customFontWeight={props.customFontWeight}
      customBorderColor={props.customBorderColor}
      {...sizeProps}
    >
      {icon && iconPosition === 'left' && <span className='icon-left'>{icon}</span>}
      <span>{props.text}</span>
      {icon && iconPosition === 'right' && <span className='icon-right'>{icon}</span>}
    </ButtonPrimaryContainer>
  );
};

export default ButtonPrimary;
